import { gql, useLazyQuery } from "@apollo/client";
import type { SearchQuerySuggestion } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import { debounce as _debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IPredictiveSearchProduct, IPredictiveSearchQuery } from "@repo/shopify/queries/search-gql";
import { predictiveSearchQuery } from "@repo/shopify/queries/search-gql";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Image from "@atoms/Image";
import Input from "@atoms/Input";
import Link from "@atoms/Link";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useCheckLocation from "@hooks/useCheckLocation";

import { useLayoutStore } from "@store/layout";

interface IFormData {
  search: string;
}

export default function HeaderSearch() {
  const navigate = useNavigate();

  const { openToast, openSearch } = useLayoutStore();

  const { isPC } = useCheckDevice();
  const { isDanielJohnston } = useCheckLocation();

  const [suggestions, setSuggestions] = useState<SearchQuerySuggestion[]>([]);
  const [searchProducts, setSearchProducts] = useState<IPredictiveSearchProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset, setFocus, watch } = useForm<IFormData>();

  const { search } = watch();

  const [fetchPredictive] = useLazyQuery<IPredictiveSearchQuery>(gql(predictiveSearchQuery.query), {
    onCompleted: (data) => {
      setIsLoading(false);
      setSearchProducts(data?.predictiveSearch?.products?.filter((node: IPredictiveSearchProduct) => node?.hidden?.value !== "true"));
      setSuggestions(data?.predictiveSearch?.queries || []);
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    setFocus("search");
  }, []);

  const handleSearchValue = async () => {
    if (search) {
      setIsLoading(true);
      await fetchPredictive({
        variables: {
          query: search,
          limit: 10,
          limitScope: "EACH",
          searchableFields: ["TITLE", "VENDOR", "PRODUCT_TYPE", "BODY"],
          types: ["QUERY", "PRODUCT"],
          unavailableProducts: "LAST",
        },
      });
    }
  };

  const onSubmit = handleSubmit(
    () => {
      if (search) {
        navigate(`${URLS.SEARCH}?q=${search}`);
        reset();
        openSearch(false);
      }
    },
    () => {
      openToast(NOTIFICATIONS.ERROR_SEARCH);
    },
  );

  return (
    <div
      className={classNames(
        "pc:top-0 pc:h-auto pc:w-1/3 pc:fixed pc:bg-white pc:pt-4 pc:p-5 top-12 right-0 z-60 flex max-h-dvh w-full flex-col gap-10 lg:w-1/4 dark:bg-black dark:text-white",
        isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
      )}
    >
      <div className="flex flex-col gap-5">
        {isPC ? (
          <div className="flex justify-between">
            <span>Search Items</span>
            <Button
              as="text"
              className="pc:visible invisible"
              onClick={() => {
                openSearch(false);
              }}
            >
              Close
            </Button>
          </div>
        ) : null}
        <form
          className={classNames("relative flex flex-col gap-2", isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black")}
          onSubmit={onSubmit}
        >
          <Input
            {...register("search", {
              required: true,
              onChange: _debounce(() => {
                void handleSearchValue();
              }, 200),
            })}
            id="search"
            innerClassName="text-[20px]!"
            placeholder="Type to search items"
          >
            {search ? (
              <Button
                as="wrapper"
                className="absolute top-1/2 right-0 -translate-y-1/2"
                onClick={() => {
                  reset();
                }}
              >
                <Icon className="text-black dark:text-white" name="Refresh" />
              </Button>
            ) : null}
          </Input>
          {search && isLoading ? (
            <span className="text-sm">Loading...</span>
          ) : (
            <>
              {search && !suggestions.length && !searchProducts.length ? (
                <span className="text-sm">{`No Result for "${search}"`}</span>
              ) : null}
            </>
          )}
        </form>
      </div>

      {search && (suggestions.length || searchProducts.length) ? (
        <section
          className={classNames(
            "pc:max-h-full pc:relative pc:top-0 pc:px-0 scrollbar-hide absolute top-24 left-0 flex max-h-[calc(100dvh-10.75rem)] w-full flex-col gap-10 overflow-y-scroll bg-white px-4",
            isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
          )}
        >
          {suggestions.length ? (
            <div className="flex flex-col gap-2.5">
              <span className="text-title">Suggestions</span>
              <div className="grid grid-cols-2 gap-y-2">
                {suggestions.map((result) => (
                  <Link
                    as="text"
                    className="capitalize"
                    href={`${URLS.SEARCH}?q=${result.text}`}
                    key={result.text}
                    onClick={() => {
                      openSearch(false);
                    }}
                  >
                    {result.text}
                  </Link>
                ))}
              </div>
            </div>
          ) : null}

          {searchProducts.length ? (
            <div className="flex flex-col gap-2.5">
              <span className="text-title">Products</span>
              <div className="tnt-gap-x pc:grid-cols-3 pc:gap-y-10 grid grid-cols-2 gap-x-2 gap-y-2">
                {searchProducts
                  .filter((result) => !result.vendor.includes("GWP"))
                  .map((result) => (
                    <Link
                      as="wrapper"
                      className="pc:flex-col flex! h-fit gap-2.5"
                      href={`${URLS.PRODUCTS}/${result.handle}`}
                      key={result.id}
                      onClick={() => {
                        openSearch(false);
                      }}
                    >
                      <Image
                        className="pc:w-auto pc:min-w-auto h-fit w-20 min-w-20"
                        height={result.featuredImage?.height || 1707}
                        src={result.featuredImage?.url}
                        width={result.featuredImage?.width || 1280}
                      />
                      <span className="text-left">{result.title}</span>
                    </Link>
                  ))}
              </div>
              <div className="sticky bottom-0 flex gap-2 pt-2.5">
                {!isPC && (
                  <Button
                    as="square"
                    className={classNames(isDanielJohnston ? "dark:bg-dj-black" : "bg-white dark:bg-black")}
                    onClick={() => {
                      reset();
                    }}
                    variant="secondary"
                  >
                    Close
                  </Button>
                )}
                <Button
                  as="square"
                  className="w-full"
                  onClick={() => {
                    if (search) {
                      navigate(`${URLS.SEARCH}?q=${search}`);
                      reset();
                      openSearch(false);
                    }
                  }}
                >
                  {`Result for "${search}"`}
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
